import { Box, Text, useStdin } from 'ink';
import SelectInput from '../../../components/SelectInput';
import { ReactNode, useEffect, useState } from 'react';
import type { Build } from '@todesktop/shared';
import CancelBuild, { useCancellation } from '../../../components/CancelBuild';
import CustomSelectInputIndicator from '../../../components/CustomSelectInputIndicator';
import CustomSelectInputItem from '../../../components/CustomSelectInputItem';
import ErrorDisplay from '../../../components/ErrorDisplay';
import ViewBuild from '../../../components/ViewBuild';
import {
  isBuildCancellable,
  isBuildRunning,
} from '../../../utilities/buildStatus';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from 'firebase/firestore';
import firestore from '../../../utilities/firestore';
import getProjectConfig from '../../../utilities/projectConfig/getProjectConfig';
import logger from '../../../utilities/logger';
import useInput from '../../../utilities/useInput';
import useExit from '../../../utilities/useExit';
import isCi from 'is-ci';
import findAppUserId from '../../../utilities/findAppUserId';
import { readQueryDocument } from '../../../utilities/firestoreData';

export default function OngoingBuildGuard({
  children,
  commandUsed,
  configPath,
}: {
  children: ReactNode;
  commandUsed: string;
  configPath?: string;
}) {
  const { isRawModeSupported } = useStdin();
  const exit = useExit();
  const [{ appId, builds, error, isLoading, itemChosen }, setState] = useState<{
    appId: string | null;
    builds: Build[];
    error: Error | null;
    isLoading: boolean;
    itemChosen: { value: string } | null;
  }>({
    appId: null,
    builds: [],
    error: null,
    isLoading: true,
    itemChosen: null,
  });

  const cancellation = useCancellation({
    appId: appId ?? undefined,
    build: builds.find(Boolean),
    autoExit: false,
  });

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    (async () => {
      try {
        const applicationId = getProjectConfig(configPath).config.id;
        const { id } = await findAppUserId(applicationId);

        const now = new Date();
        const twentyFourHoursAgo = new Date(
          now.getTime() - 24 * 60 * 60 * 1000,
        );

        const buildsQuery = query(
          collection(
            firestore,
            `users/${id}/applications/${applicationId}/builds`,
          ),
          where('createdAt', '>=', twentyFourHoursAgo.toISOString()),
          orderBy('createdAt', 'desc'),
          limit(10),
        );
        const buildsResult = await getDocs(buildsQuery);

        const stateUpdates: {
          appId: string | null;
          isLoading: boolean;
          builds: Build[];
        } = {
          appId: applicationId,
          isLoading: false,
          builds: [],
        };

        if (!buildsResult.empty) {
          const latestBuild = readQueryDocument<Build>(buildsResult.docs[0]);
          const isRunning =
            isBuildRunning(latestBuild) && latestBuild.status !== 'preparation';
          logger.debug(
            { isRunning },
            'OngoingBuildGuard component: got latest build',
          );

          stateUpdates.builds = buildsResult.docs
            .map((doc) => readQueryDocument<Build>(doc))
            .filter((build) => {
              if (isBuildRunning(build) && build.status !== 'preparation') {
                return true;
              }
              return false;
            });
        }

        setState((previousState) => ({
          ...previousState,
          ...stateUpdates,
        }));
      } catch (e) {
        setState((previousState) => ({
          ...previousState,
          error: e as Error,
        }));
      }
    })();
  }, [builds, itemChosen, isLoading, isRawModeSupported, configPath]);

  /*
    Because we're using <SelectInput/>, we need to explicitly handle when
    ctrl+c is pressed. `useInput` takes care of that.
    See https://github.com/vadimdemedes/pastel/issues/38
  */
  useInput(() => {}, { isActive: Boolean(isRawModeSupported) });

  if (error) {
    return <ErrorDisplay commandUsed={commandUsed} error={error} />;
  }

  if (isLoading) {
    return <Text>...</Text>;
  }

  if (itemChosen) {
    logger.debug({ itemChosen }, 'OngoingBuildGuard component: item chosen');
    const build = builds.find(Boolean);

    if (itemChosen.value === 'view' && build) {
      return <ViewBuild commandUsed={commandUsed} id={build.id} />;
    } else if (itemChosen.value === 'cancel' && build && appId) {
      if (cancellation.status === 'cancelled') {
        return <>{children}</>;
      }
      return (
        <CancelBuild
          buildId={build.id}
          cancellation={cancellation}
          commandUsed={commandUsed}
        />
      );
    } else if (itemChosen.value === 'concurrent') {
      return <>{children}</>;
    } else {
      setTimeout(exit, 10);
    }
  }

  if (builds.length && isCi === false && isRawModeSupported) {
    const latestBuild = builds.find(Boolean);
    const multiple = builds.length > 1;

    const items = [
      {
        label: `View ${multiple ? 'latest' : 'it'}`,
        value: 'view',
      },
    ];

    if (builds.some((build) => isBuildCancellable(build))) {
      items.push({
        label: `Cancel ${multiple ? 'latest' : 'it'} and start a new build`,
        value: 'cancel',
      });

      items.push({
        label: 'Start a concurrent build',
        value: 'concurrent',
      });
    }

    items.push({
      label: 'Exit',
      value: 'exit',
    });
    const handleSelect = (selectedItem: { value: string }) => {
      if (selectedItem.value === 'cancel') {
        cancellation.cancel();
      }

      setState((previousState) => ({
        ...previousState,
        itemChosen: selectedItem,
      }));
    };

    return (
      <>
        <Box marginBottom={1}>
          <Text bold>
            {multiple
              ? 'There are ongoing builds '
              : 'There is an ongoing build '}
          </Text>
          <Text>
            ({latestBuild?.appName}
            {latestBuild?.appVersion ? ` v${latestBuild.appVersion}` : ''})
          </Text>
        </Box>
        <SelectInput
          indicatorComponent={CustomSelectInputIndicator}
          itemComponent={CustomSelectInputItem}
          items={items}
          onSelect={handleSelect}
        />
      </>
    );
  } else {
    return <>{children}</>;
  }
}
